/**
 * `/recordings/$source/$id` — the canonical shareable link for every replay.
 *
 * `$source` is validated inside the page, not here: an unknown source has to
 * render the same not-found state as a missing or RLS-denied row, and a route
 * that refused to match would instead fall through to the app's generic 404 and
 * tell a link-prober something different from what a denied row tells them.
 *
 * Gated exactly like the library and the rest of community (`requireAlfieCapability`).
 * The detail skeleton is both the route `pendingComponent` (which covers the
 * capability check and the chunk download, before the component exists) and the
 * in-component loading state, so the whole wait reads as one grey page.
 */

import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { requireAlfieCapability } from '@/api/alfie-plan';
import { RecordingDetailSkeleton } from '@/components/recordings/RecordingsSkeletons';

const RecordingDetail = lazy(() => import('@/pages/RecordingDetail'));

export const Route = createFileRoute('/recordings/$source/$id')({
  beforeLoad: ({ context: { queryClient } }) => requireAlfieCapability(queryClient, 'community'),
  pendingComponent: RecordingDetailSkeleton,
  component: RecordingDetailRoute,
});

function RecordingDetailRoute() {
  const { source, id } = Route.useParams();
  return (
    <Suspense fallback={<RecordingDetailSkeleton />}>
      <RecordingDetail source={source} id={id} />
    </Suspense>
  );
}
